import React, { useEffect, useState, useCallback, lazy, Suspense } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import "@/i18n";
import "../styles/font.scss";
import { LoginRoute, PasswordForgottenRoute, PasswordResetRoute, RegisterPageRoute } from "@/routes/auth";
import { baseUrl as dashBoardRoute } from "@/routes/dashboard";
import { Layout } from "@layout/Layout";
import { Dashboard } from "@/App/components/pages/Dashboard/Dashboard";
import "./assets/fonts/remindme-categories/css/remindme-categories.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ErrorBoundary from "@components/ErrorBoundary/ErrorBoundary";
import LoginOrRegister from "@/auth/components/LoginOrRegister/LoginOrRegister";
import AppContext from "./context/AppContext";
import { NotFound } from "./components/pages/NotFound/NotFound";
import { useFlavour } from "../shared/flavours/useFlavour";
import Homepage from "./components/@remote/Homepage/Homepage";
import { SofPage, ToastContainer, PasswordForgotten, PasswordReset } from "./components/@remote";
import { useTracking } from "../tracking/tracking/Tracking";
import { getUserId, isAuthenticated } from "@/api/localStorage";
import { Logout } from "./components/pages/Logout/Logout";
import { ProtectedComponent } from "./components/ProtectedComponent";

import ApolloWrapper from "./components/ApolloWrapper";
const LazyApolloWrapper = lazy(() => import("./components/ApolloWrapper"));
const LazyMissingData = lazy(() => import("@/changeservice/components/MissingData"));
const LazyOverview = lazy(() => import("@/offer/components/offers/Overview"));
const LazyFaqPage = lazy(() => import("@/cms/components/FaqPage/components/FaqPage"));
const LazyODP = lazy(() => import("@/offer/components/ODP/ODP"));
const LazyNewODP = lazy(() => import("@/offerDetailPage/ODP"));
const LazyProfileForm = lazy(() => import("@/auth/components/MyProfile/components/ProfileForm/ProfileForm"));
const LazyRewards = lazy(() => import("@/cms/components/Rewards/components/Rewards"));
const LazyTopDealsPage = lazy(() => import("@/cms/components/TopDeals/components/TopDealsPage/TopDealsPage"));
const LazyTopDealPage = lazy(() => import("@/cms/components/TopDeals/components/TopDealPage"));
const LazyFeedbackPage = lazy(() => import("@/changeservice/components/Feedback/FeedbackPage"));
const LazyVerify = lazy(() => import("@/changeservice/components/Verify"));

import {
  ChangeServiceDetailsRoute,
  ChangeServiceOverviewRoute,
  ChangeServiceRoute,
  ChangeServiceTariffDetailsRoute,
  DocumentsRoute,
  ChangeServiceTariffHistoryRoute,
  ChangeServiceEditRoute,
  deactivateRoute,
  withdrawalRoute,
  ChangeServiceRouteNew,
  VerifyRoute,
} from "../routes/changeservice";
import ChangeServiceOverview from "./components/@remote/ChangeServiceOverview/ChangeServiceOverview";
import Deactivate from "./components/@remote/Deactivate/Deactivate";
import { ChangeServiceDetails } from "./components/pages/ChangeServiceDetails/ChangeServiceDetails";
import { Withdrawal } from "./components/pages/Withdrawal/Withdrawal";
import { Documents } from "./components/pages/Documents/Documents";
import { ChangeServiceTariffDetails } from "./components/pages/ChangeServiceTariffDetails/ChangeServiceTariffDetails";
import { ChangeServiceTariffHistory } from "./components/pages/ChangeServiceTariffHistory/ChangeServiceTariffHistory";
import { ChangeServiceEdit } from "./components/pages/ChangeServiceEdit/ChangeServiceEdit";
import PageBuilder from "./components/@remote/PageBuilder/PageBuilder";
import { PageBuilderRoute } from "../routes/cms";
import DyanmicLandingPage from "./components/@remote/DynamicLandingPage/DynamicLandingPage";
import FlexibleLandingPage from "./components/@remote/FlexibleLandingPage/FlexibleLandingPage";
import { BlogCategorieRoutes } from "@/cms/components/BlogPages/misc/Routes";
import BlogOverview from "./components/@remote/BlogOverview/BlogOverview";
import BlogArticleQuery from "./components/@remote/BlogArticleQuery/BlogArticleQuery";
import BlogCategoryOverView from "./components/@remote/BlogCategoryOverview/BlogCategoryOverview";
import { t } from "i18next";
import { ChangeServiceNew } from "./components/pages/ChangeServiceNew/ChangeServiceNew";
import { topDealsDetailRoute } from "@/cms/components/TopDeals/misc/Routes";
import { BonusRoute } from "@/routes/bonus";
import { MyProfileRoute } from "@/routes/myProfile";
import Impersonate from "@/auth/components/Impersonate/Impersonate";
import { FragenBaseRoute, InternalBaseRoute } from "@/routes/content";
import ContentPages from "@/cms/components/ContentPages/components/ContentPages";
import { SleepingCustomerRoute } from "@/changeservice/misc/routes";
import ConnectedSleepingCustomer from "@/SleepingCustomer/ConnectedSleepingCustomer";
import { electricityRoute, gasRoute } from "@/routes/dynamicLandingpages";
import { NewsletterCallbackRoute } from "@/cms/components/Newsletter/misc/Routes";
import Callback from "@/cms/components/Newsletter/misc/Callback";
import ScrollToTop from "./ScrollToTop";
import Passwordless from "@/auth/components/Passwordless/Passwordless";
import { isDev } from "@/utils/utils";
import { useCheckAppVersion } from "@/shared/hooks/useCheckAppVersion";
import ReferFriend from "./components/pages/ReferFriend/ReferFriend";
import ReferralWidget from "./components/ReferralWidget/ReferralWidget";
import useReferralCode from "@/shared/hooks/useReferralCode";
import { useIsDesktop } from "@/cms/utils";

export const App = () => {
  const { Favicon, title, description } = useFlavour();
  const [loggedIn, setLoggedIn] = useState(isAuthenticated());
  const [impersonatorToken, setImpersonatorToken] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  useReferralCode();
  useCheckAppVersion();
  useTracking();

  // Listen to RouteEvent
  const handleRouteEvent = useCallback(
    (event: CustomEvent) => {
      let queryString = "";
      if (event.detail?.queryParams) {
        queryString = Object.keys(event.detail.queryParams)
          .map((key) => key + "=" + encodeURIComponent(event.detail.queryParams[key]))
          .join("&");
      }
      const path = event.detail?.path + (queryString ? "?" + queryString : "");
      if (path) navigate(path);
    },
    [navigate]
  );

  useEffect(() => {
    window.addEventListener("AuthEvent", () => setLoggedIn(isAuthenticated()));
    window.addEventListener("RouteEvent", handleRouteEvent as EventListener);

    return () => {
      window.removeEventListener("AuthEvent", () => setLoggedIn(isAuthenticated()));
      window.removeEventListener("RouteEvent", handleRouteEvent as EventListener);
    };
  }, [handleRouteEvent]);

  useEffect(() => {
    if (!isDev()) {
      setTimeout(() => {
        const script = document.createElement("script");

        script.setAttribute("chat", "true");
        script.setAttribute("widgetId", "4f19c8bb-d85e-4b62-8d99-2beb84c77509");
        script.src = "//eu.fw-cdn.com/11898752/444020.js";
        script.async = true;

        if (document.body) {
          document.body.appendChild(script);
        }
      }, 30000);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line
    // @ts-ignore-next-line
    window._conv_q = window._conv_q || [];
    // eslint-disable-next-line
    // @ts-ignore-next-line
    window._conv_q.push(["pageView"]);
    // eslint-disable-next-line
    // @ts-ignore-next-line
    window._conv_q.push(["run", "true"]);
  }, [location.pathname]);

  useEffect(() => {
    // eslint-disable-next-line
    const userID = getUserId();
    // eslint-disable-next-line
    // @ts-ignore-next-line
    (window.CE_API || (window.CE_API = [])).push(function () {
      // eslint-disable-next-line
      // @ts-ignore-next-line
      window.CE2.identify(userID);
    });
  }, []);

  return (
    <ErrorBoundary key={location.pathname}>
      {/* <CookieConsent /> */}
      <ToastContainer />
      <AppContext.Provider value={{ loggedIn, setLoggedIn, setImpersonatorToken, impersonatorToken }}>
        <Helmet>
          <link rel="shortcut icon" href={Favicon} />
          <title>{title}</title>
          <meta name="description" content={description} />
        </Helmet>
        <Layout>
          <ErrorBoundary key={location.pathname}>
            <AppRoutes loggedIn={loggedIn} />
            <ReferralWidget />
          </ErrorBoundary>
        </Layout>
      </AppContext.Provider>
    </ErrorBoundary>
  );
};

const AppRoutes = ({ loggedIn }: { loggedIn: boolean }) => {
  const isDesktop = useIsDesktop();

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route
          path="/"
          element={
            <Suspense fallback={<div></div>}>
              <LazyApolloWrapper component={<Homepage />} />
            </Suspense>
          }
        />
        <Route path={LoginRoute} element={!loggedIn ? <LoginOrRegister /> : <Navigate to={dashBoardRoute} />} />
        <Route
          path={MyProfileRoute}
          element={
            <Suspense fallback={<div></div>}>
              <ProtectedComponent isAuthenticated={loggedIn} component={<LazyProfileForm />} />
            </Suspense>
          }
        />

        <Route path={NewsletterCallbackRoute} element={<Callback />} />

        <Route path={RegisterPageRoute} element={<LoginOrRegister />} />
        <Route path={PasswordForgottenRoute} element={<PasswordForgotten />} />
        <Route path={PasswordResetRoute} element={<PasswordReset />} />
        <Route path="/tarifoptimierung" element={<SofPage />} />
        <Route
          path={dashBoardRoute}
          element={<ProtectedComponent isAuthenticated={loggedIn} component={<Dashboard />} />}
        />
        <Route path="/jobs/:pageId" element={<ApolloWrapper component={<Homepage slug="jobseite" />} />} />
        <Route path={"/mein-remindme/freunde-werben-freunde"} element={<ReferFriend />} />

        <Route path="/jobs/:pageId/:title" element={<ApolloWrapper component={<Homepage slug="jobseite" />} />} />
        <Route
          path={topDealsDetailRoute}
          element={
            <Suspense fallback={<div></div>}>
              <ApolloWrapper component={<LazyTopDealPage />} />
            </Suspense>
          }
        />
        <Route
          path="/top-deals"
          element={
            <Suspense fallback={<div></div>}>
              <ApolloWrapper component={<LazyTopDealsPage />} />
            </Suspense>
          }
        />
        <Route
          path="/feedback"
          element={
            <Suspense fallback={<div></div>}>
              <ProtectedComponent isAuthenticated={loggedIn} component={<LazyFeedbackPage />} />
            </Suspense>
          }
        />
        <Route
          path={BonusRoute}
          element={
            <Suspense fallback={<div></div>}>
              <ApolloWrapper component={<LazyRewards />} />
            </Suspense>
          }
        />

        <Route
          path={PageBuilderRoute}
          element={<ApolloWrapper component={<PageBuilder builder="landingPageBuilder" />} />}
        />

        <Route
          path={FragenBaseRoute}
          element={
            <ApolloWrapper
              component={
                <Suspense fallback={""}>
                  <LazyFaqPage />
                </Suspense>
              }
            />
          }
        />
        <Route
          path={`${FragenBaseRoute}/:pageName`}
          element={
            <ApolloWrapper
              component={
                <Suspense fallback={""}>
                  <LazyFaqPage />
                </Suspense>
              }
            />
          }
        />
        <Route
          path={`${FragenBaseRoute}/*/:pageName`}
          element={
            <ApolloWrapper
              component={
                <Suspense fallback={""}>
                  <LazyFaqPage />
                </Suspense>
              }
            />
          }
        />
        <Route
          path={`${FragenBaseRoute}/*/*/:pageName`}
          element={
            <ApolloWrapper
              component={
                <Suspense fallback={""}>
                  <LazyFaqPage />
                </Suspense>
              }
            />
          }
        />
        <Route
          path={`${FragenBaseRoute}/*/*/*/:pageName`}
          element={
            <ApolloWrapper
              component={
                <Suspense fallback={""}>
                  <LazyFaqPage />
                </Suspense>
              }
            />
          }
        />

        <Route path="/lp/:slug" element={<ApolloWrapper component={<DyanmicLandingPage />} />} />
        <Route path="/dlp/:slug" element={<ApolloWrapper component={<FlexibleLandingPage />} />} />
        <Route path="/gdlp/:slug" element={<ApolloWrapper component={<FlexibleLandingPage />} />} />
        <Route
          path={electricityRoute}
          element={<ApolloWrapper component={<FlexibleLandingPage customSlug="strompreisvergleich-sof-neu" />} />}
        />
        <Route
          path={gasRoute}
          element={<ApolloWrapper component={<FlexibleLandingPage customSlug="gaspreisvergleich-sof-neu" />} />}
        />

        <Route
          path="/fehlendedaten/:category/:changeServiceId"
          element={
            <ProtectedComponent
              isAuthenticated={loggedIn}
              component={
                <Suspense fallback={""}>
                  <LazyMissingData />
                </Suspense>
              }
            />
          }
        />
        <Route
          path={SleepingCustomerRoute}
          element={<ProtectedComponent isAuthenticated={loggedIn} component={<ConnectedSleepingCustomer />} />}
        />

        <Route path="/erinnerung-erstellen/kategorie" element={<div>Erinnerung erstellen</div>} />
        <Route
          path="/erinnerung-erstellen/kategorie/:category"
          element={<div>Erinnerung erstellen mit Kategorie</div>}
        />
        <Route path="/kontakt" element={<ApolloWrapper component={<Homepage slug="kontakt" />} />} />
        <Route path="/unternehmen" element={<ApolloWrapper component={<Homepage slug="unternehmensseite" />} />} />
        <Route path="/unternehmen/jobs" element={<ApolloWrapper component={<Homepage slug="karriereseite" />} />} />

        {/* Internal Pages */}
        <Route path={InternalBaseRoute + "/:pageName"} element={<ApolloWrapper component={<ContentPages />} />} />
        <Route path={InternalBaseRoute + "/*/:pageName"} element={<ApolloWrapper component={<ContentPages />} />} />
        <Route path={InternalBaseRoute + "/*/*/:pageName"} element={<ApolloWrapper component={<ContentPages />} />} />
        <Route path={InternalBaseRoute + "/*/*/*/:pageName"} element={<ApolloWrapper component={<ContentPages />} />} />

        <Route path="/internal/impressum" element={<ApolloWrapper component={<Homepage slug="impressum" />} />} />
        <Route
          path="/internal/datenschutzerklaerung"
          element={<ApolloWrapper component={<Homepage slug="datenschutzerklaerung" />} />}
        />
        <Route path="/ratgeber" element={<ApolloWrapper component={<BlogOverview />} />} />
        <Route
          path={"/:category" + "/ratgeber" + "/:article"}
          element={<ApolloWrapper component={<BlogArticleQuery />} />}
        />
        {BlogCategorieRoutes.map((cat, index) => (
          <Route
            key={"R_" + index}
            path={cat}
            element={<ApolloWrapper component={<BlogCategoryOverView category={cat} t={t} />} />}
          />
        ))}
        <Route path="/presse" element={<ApolloWrapper component={<Homepage slug="presse" />} />} />
        <Route path="/partner-werden" element={<ApolloWrapper component={<Homepage slug="partner-werden" />} />} />
        <Route path="/fehler" element={<NotFound />} />
        <Route path="/logout" element={<Logout />} />
        <Route
          path="/impersonate"
          element={<ProtectedComponent isAuthenticated={loggedIn} component={<Impersonate />} />}
        />
        <Route path="/login-link" element={<Passwordless />} />
        <Route path={ChangeServiceRouteNew} element={<ChangeServiceNew />} />
        <Route path={ChangeServiceRoute} element={<ChangeServiceNew />} />
        <Route
          path={VerifyRoute}
          element={
            <ProtectedComponent
              isAuthenticated={loggedIn}
              component={
                <Suspense fallback={""}>
                  <LazyVerify />
                </Suspense>
              }
            />
          }
        />

        <Route
          path={ChangeServiceOverviewRoute}
          element={<ProtectedComponent isAuthenticated={loggedIn} component={<ChangeServiceOverview />} />}
        />
        <Route
          path={deactivateRoute}
          element={<ProtectedComponent isAuthenticated={loggedIn} component={<Deactivate />} />}
        />
        <Route
          path={ChangeServiceDetailsRoute}
          element={<ProtectedComponent isAuthenticated={loggedIn} component={<ChangeServiceDetails />} />}
        />
        <Route
          path={DocumentsRoute}
          element={<ProtectedComponent isAuthenticated={loggedIn} component={<Documents />} />}
        />
        <Route
          path={withdrawalRoute}
          element={<ProtectedComponent isAuthenticated={loggedIn} component={<Withdrawal />} />}
        />
        <Route
          path={ChangeServiceTariffDetailsRoute}
          element={<ProtectedComponent isAuthenticated={loggedIn} component={<ChangeServiceTariffDetails />} />}
        />
        <Route
          path={ChangeServiceTariffHistoryRoute}
          element={<ProtectedComponent isAuthenticated={loggedIn} component={<ChangeServiceTariffHistory />} />}
        />
        <Route
          path={ChangeServiceEditRoute}
          element={<ProtectedComponent isAuthenticated={loggedIn} component={<ChangeServiceEdit />} />}
        />
        <Route
          path="/mein-remindme/angebote"
          element={
            <Suspense fallback={<div></div>}>
              <ProtectedComponent isAuthenticated={loggedIn} component={<LazyOverview />} />
            </Suspense>
          }
        />
        <Route
          path="/mein-remindme/angebote/new/:category/:id"
          element={
            <Suspense fallback={<div></div>}>
              <ProtectedComponent isAuthenticated={loggedIn} component={<LazyNewODP />} />
            </Suspense>
          }
        />

        <Route
          path="/mein-remindme/angebote/:category/:id"
          element={
            <Suspense fallback={<div></div>}>
              <ProtectedComponent isAuthenticated={loggedIn} component={isDesktop ? <LazyNewODP /> : <LazyODP />} />
            </Suspense>
          }
        />

        <Route path="*" element={<Navigate to={loggedIn ? dashBoardRoute : "/fehler"} />} />
      </Routes>
    </>
  );
};
